import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseTable } from '@/components';
import { useAssetsAllocationTable } from '@/components/pages/SettingsPage/MainContent/UserManagement/AssetsAllocation/useAssetsAllocationTable';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionEmptyState } from '@/components/pages/AssetsPreviewPage/AssetsOverview/EmptyStates';
import { useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { dispatch } from '@/store/store';
import { updateAssetsAllocationCache } from '@/components/pages/SettingsPage/MainContent/UserManagement/AssetsAllocation/utils';

export interface AssetsAllocationTableRef {
  triggerSave: () => void;
}

interface AssetsAllocationTableProps {
  setIsDirty: any;
}

export const AssetsAllocationTable = forwardRef<AssetsAllocationTableRef, AssetsAllocationTableProps>(
  ({ setIsDirty }, ref) => {
    const { t } = useTranslation();
    const { userId } = useParams<{ userId: string }>();
    const { columns, data, isLoadingAssetsAllocation, updateAssetsAllocation } = useAssetsAllocationTable();
    const [currentData, setCurrentData] = useState<any[]>(data);

    const updateData = (rowIndex: number, columnId: string, value: boolean) => {
      setCurrentData((oldData: any[]) =>
        oldData.map((row: any, index: number) => {
          if (index === rowIndex) {
            return {
              ...row,
              [columnId]: value,
            };
          }
          return row;
        })
      );
    };

    useImperativeHandle(ref, () => ({
      triggerSave: async () => {
        dispatch(updateAssetsAllocationCache(currentData, userId));
        setIsDirty(false);
        await updateAssetsAllocation({ userId, body: currentData });
      },
    }));

    useEffect(() => {
      setIsDirty(!isEqual(currentData, data));
    }, [currentData, data]);

    useEffect(() => {
      setCurrentData(data ?? []);
    }, [data]);

    if (isLoadingAssetsAllocation) {
      return <Skeleton className="h-[calc(100vh-15.3rem)]" />;
    }

    if (!data?.length) {
      return <SectionEmptyState text={t('general.noData')} />;
    }

    return (
      <BaseTable
        columns={columns}
        data={currentData}
        wrapHeaders={false}
        meta={{ updateData }}
        classNames={{
          card: {
            container: 'h-full w-full overflow-x-auto max-h-full max-w-[calc(100%)] overflow-x-auto rounded-lg',
            content: 'p-0 m-2 overflow-y-hidden max-h-full',
          },
          table: {
            container:
              'overflow-auto h-full w-full sticky top-0  min-h-[calc(100vh-16.3rem)] max-h-[calc(100vh-15.8rem)]',
            th: '[&>*]:text-left [&>*]:text-subtext [&>*]:font-medium [&>*]:text-[14px] py-2',
            tr: 'h-14 bg-white ',
            td: 'group-hover:bg-gray-100 text-[14px]',
          },
        }}
      />
    );
  }
);

AssetsAllocationTable.displayName = 'AssetsAllocationTable';
